import client from '@config/client'
import API_URL from '@config/services'
import {
  ApiClientResponse,
  Profile,
  ProfileUpdatePayload,
  TwoFactorAuthPayload,
  TwoFactorAuthResponse,
  User
} from '../types/api'

/**
 * Updates the current user's profile
 * @param payload - Profile data to update
 * @returns API response
 */
export const updateProfile = (
  payload: ProfileUpdatePayload
): ApiClientResponse<Profile> => {
  return client
    .patch(API_URL.PROFILES.GET_PROFILES_PROFILE, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Updates user account information
 * @param payload - User data to update
 * @returns API response
 */
export const updateUser = (payload: Partial<User>): ApiClientResponse<User> => {
  return client
    .patch(API_URL.PROFILES.PATCH_PROFILES_USER, { ...payload })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}

/**
 * Activates two-factor authentication for the current user
 * @param payload - 2FA activation data
 * @returns API response with QR code and setup information
 */
export const activate2fa = (
  payload: TwoFactorAuthPayload = {}
): ApiClientResponse<TwoFactorAuthResponse> => {
  return client
    .post(API_URL.PROFILES.POST_ACTIVATE_2FA, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Deactivates two-factor authentication for the current user
 * @param payload - 2FA deactivation data
 * @returns API response
 */
export const deactivate2fa = (
  payload: TwoFactorAuthPayload = {}
): ApiClientResponse<any> => {
  return client
    .post(API_URL.PROFILES.POST_DISABLE_2FA, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Verifies two-factor authentication code
 * @param payload - 2FA verification data (code)
 * @returns API response with verification status
 */
export const verify2fa = (
  payload: TwoFactorAuthPayload = {}
): ApiClientResponse<TwoFactorAuthResponse> => {
  return client
    .post(API_URL.PROFILES.POST_VERIFY_2FA, payload)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Removes verification status from 2FA
 * @returns API response
 */
export const unverify2fa = (): ApiClientResponse<any> => {
  return client
    .post(API_URL.PROFILES.POST_UNVERIFY_2FA)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Obtains a 2FA token
 * @returns API response with 2FA token
 */
export const obtain2faToken = (): ApiClientResponse<{ token: string }> => {
  return client
    .post(API_URL.PROFILES.POST_OBTAIN_TOKEN)
    .then(response => response)
    .catch(error => error.response)
}

/**
 * Invites a user to the organization
 * @param email - User email address
 * @param otherParams - Additional user information
 * @returns API response
 */
export const inviteUser = (
  email: string,
  otherParams: Record<string, any> = {}
): ApiClientResponse<any> => {
  return client
    .post(API_URL.PROFILES.POST_INVITE_USER, { email, ...otherParams })
    .then(response => response)
    .catch(error => {
      return error.response
    })
}
